"use client";

import React from "react";
import { ArrowLeft } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";


declare const window: {
  wvcClient: {
    getCurrentWPQueryParams: () => Record<string, any>;
  };
};

function SingleExperienceConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 md:py-24">
        <div className="mb-8 flex flex-col items-center space-y-4">
          <div className="h-10 w-3/4 animate-pulse rounded-md bg-muted/50 md:w-1/2"></div>
        </div>
        <div className="mb-8 aspect-video w-full animate-pulse rounded-2xl bg-muted/30"></div>
        <div className="mx-auto max-w-2xl space-y-4">
          <div className="h-4 w-32 animate-pulse rounded-md bg-muted/50"></div>
          <div className="h-4 w-full animate-pulse rounded-md bg-muted/50"></div>
          <div className="h-4 w-full animate-pulse rounded-md bg-muted/50"></div>
          <div className="h-4 w-5/6 animate-pulse rounded-md bg-muted/50"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center md:py-24">
        <p className="text-muted-foreground">Unable to load experience details. Please try again later.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center md:py-24">
        <h1 className="font-default mb-4 text-3xl font-light text-foreground">Experience Not Found</h1>
        <p className="mb-8 text-muted-foreground">The experience you are looking for does not exist or has been removed.</p>
        <Link to="#">
          <Button variant="outline" className="rounded-full">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Experiences
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  
  
  const title = post.customFields?.title || post.title;
  const description = post.customFields?.description || post.content;
  const imageSrc = post.customFields?.image || post.featuredImage;
  const date = post.customFields?.date || post.date;

  
  const formattedDate = date ? new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : null;

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 md:py-24">
      <header className="mb-10 text-center">
        <h1 className="font-default mb-6 text-4xl font-light tracking-tight text-foreground md:text-5xl lg:text-6xl">
          {title}
        </h1>
      </header>

      {imageSrc && (
        <div className="mb-12 overflow-hidden rounded-2xl shadow-sm">
          <img
            src={imageSrc}
            alt={title}
            className="aspect-video w-full object-cover transition-transform duration-700 hover:scale-105"
            loading="lazy"
          />
        </div>
      )}

      <div className="mx-auto max-w-2xl">
        {formattedDate && (
          <div className="mb-8 text-center">
            <time className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
              {formattedDate}
            </time>
          </div>
        )}

        {description && (
          <div className="font-serif text-lg leading-relaxed text-foreground/80 whitespace-pre-wrap">
            {}
            {description}
          </div>
        )}

        <Separator className="my-12 bg-border/60" />

        <div className="flex justify-center">
          <Link to="#">
            <Button variant="outline" size="lg" className="rounded-full px-8 font-medium tracking-wide transition-colors hover:bg-primary hover:text-primary-foreground">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Experiences
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function SingleExperienceTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "experience" }; 
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-experience-template-section" className="bg-background">
      <PostsProvider wp_query={wp_query}>
        <SingleExperienceConsumer />
      </PostsProvider>
    </section>
  );
}